import { Literal, Record as RuntypesRecord, Static, String } from 'runtypes';

import { decodeBase64 } from 'src/util/common';

const IDO_DETAILS_SEARCH_GROUP_OPERATIONS = ['and', 'or'] as const;
const IDO_DETAILS_SEARCH_FIELD_TYPES = [
	'value',
	'textarea',
	'singleChoice',
	'multipleChoice',
	'date',
	'geoLocation',
	'binary',
] as const;
const IDO_DETAILS_SEARCH_FIELD_LOCATIONS = ['masterData', 'contactPerson'] as const;
const IDO_DETAILS_SEARCH_FIELD_OPERATIONS = [
	'isLessThan',
	'isGreaterThan',
	'isBeginningWith',
	'isNotBeginningWith',
	'isEndingWith',
	'isNotEndingWith',
	'doesContain',
	'doesNotContain',
	'is',
	'isNot',
	'isEmpty',
	'isNotEmpty',
	'isAny',
	'isNotAny',
	'isAll',
	'isNotAll',
	'isBefore',
	'isAfter',
	'isInTheRange',
	'isNotInTheRange',
	'isInTheLast',
	'isNotInTheLast',
	'isInTheNext',
	'isNotInTheNext',
	'isInRadiusSearch',
	'isTrue',
	'isNotTrue',
] as const;

type IdoDetailsSearchGroupOperation = (typeof IDO_DETAILS_SEARCH_GROUP_OPERATIONS)[number];
export type IdoDetailsSearchFieldType = (typeof IDO_DETAILS_SEARCH_FIELD_TYPES)[number];
export type IdoDetailsSearchFieldLocation = (typeof IDO_DETAILS_SEARCH_FIELD_LOCATIONS)[number];
export type IdoDetailsSearchFieldOperation = (typeof IDO_DETAILS_SEARCH_FIELD_OPERATIONS)[number];
type IdoDetailsSearchTermEditorKind =
	| 'date'
	| 'geoLocation'
	| 'multipleChoice'
	| 'none'
	| 'singleChoice'
	| 'text'
	| 'unsupported';
type IdoDetailsSearchScalarTerm = null | number | string;
type IdoDetailsSearchTermRecordValue = IdoDetailsSearchScalarTerm | IdoDetailsSearchScalarTerm[];

export type IdoDetailsSearchTerms = IdoDetailsSearchTermRecordValue | Record<string, IdoDetailsSearchTermRecordValue>;

export const IdoDetailsSearchFieldDefinitionRunType = RuntypesRecord({
	displayField: String,
	location: Literal('masterData').Or(Literal('contactPerson')),
	type: Literal('value')
		.Or(Literal('textarea'))
		.Or(Literal('singleChoice'))
		.Or(Literal('multipleChoice'))
		.Or(Literal('date'))
		.Or(Literal('geoLocation'))
		.Or(Literal('binary')),
	valueField: String,
});

export const IdoDetailsSearchTermOptionRunType = RuntypesRecord({
	displayField: String,
	valueField: String,
});

export type IdoDetailsSearchTermOption = Static<typeof IdoDetailsSearchTermOptionRunType>;

export interface IdoDetailsSearchFieldDefinition {
	displayField: string;
	location: IdoDetailsSearchFieldLocation;
	name: string;
	type: IdoDetailsSearchFieldType;
	valueField: string;
}

export interface IdoDetailsSearchFieldCondition {
	_key?: string;
	displayField?: string;
	kind: 'fieldCondition';
	location: IdoDetailsSearchFieldLocation;
	name: string;
	operation: IdoDetailsSearchFieldOperation;
	terms: IdoDetailsSearchTerms;
	type: IdoDetailsSearchFieldType;
}

export interface IdoDetailsSearchGroup {
	_key?: string;
	children: IdoDetailsSearchNode[];
	kind: 'group';
	logicalOperator: IdoDetailsSearchGroupOperation;
}

export type IdoDetailsSearchNode = IdoDetailsSearchFieldCondition | IdoDetailsSearchGroup;

type LegacyIdoDetailsSearchFieldCondition = {
	displayField?: string;
	location: IdoDetailsSearchFieldLocation;
	name: string;
	operation: IdoDetailsSearchFieldOperation;
	terms: IdoDetailsSearchTerms;
	type: IdoDetailsSearchFieldType;
};

type LegacyIdoDetailsSearchGroup = {
	fields: Array<LegacyIdoDetailsSearchFieldCondition | LegacyIdoDetailsSearchGroup | Record<string, never>>;
	operation?: IdoDetailsSearchGroupOperation;
};

type LegacyIdoDetailsSearchPayload = [LegacyIdoDetailsSearchGroup];

const EMPTY_LEGACY_GROUP_CHILD = {};
const EMPTY_SERIALIZED_IDO_DETAILS_SEARCH = '';
const IDO_DETAILS_SEARCH_OPERATIONS_WITHOUT_TERMS = ['isEmpty', 'isNotEmpty', 'isTrue', 'isNotTrue'] as const;
const IDO_DETAILS_SEARCH_CHOICE_MULTI_VALUE_OPERATIONS = ['isAny', 'isNotAny', 'isAll', 'isNotAll'] as const;
const IDO_DETAILS_SEARCH_AUTOCOMPLETE_OPERATIONS = [
	'isBeginningWith',
	'isNotBeginningWith',
	'isEndingWith',
	'isNotEndingWith',
	'doesContain',
	'doesNotContain',
	'is',
	'isNot',
] as const;
const IDO_DETAILS_SEARCH_GEO_LOCATION_RADIUS_OPERATION = 'isInRadiusSearch' as const;
export const IDO_DETAILS_SEARCH_DATE_TYPES = ['fixed', 'dynamic'] as const;
const IDO_DETAILS_SEARCH_DATE_VALUE_OPERATIONS = ['is', 'isNot', 'isBefore', 'isAfter'] as const;
const IDO_DETAILS_SEARCH_DATE_RANGE_OPERATIONS = ['isInTheRange', 'isNotInTheRange'] as const;
const IDO_DETAILS_SEARCH_DATE_RELATIVE_OPERATIONS = [
	'isInTheLast',
	'isNotInTheLast',
	'isInTheNext',
	'isNotInTheNext',
] as const;
export const IDO_DETAILS_SEARCH_DATE_DYNAMIC_OPERATIONS = [
	'today',
	'currentCalendarWeek',
	'weekdayOfCurrentCalendarWeek',
	'currentMonth',
	'nthDayOfCurrentMonth',
	'nthWeekdayOfCurrentMonth',
	'currentQuarter',
	'currentYear',
	'quarterOfCurrentYear',
	'monthOfCurrentYear',
] as const;
export const IDO_DETAILS_SEARCH_DATE_RELATIVE_RANGES = ['day', 'week', 'month', 'quarter', 'year'] as const;
export const IDO_DETAILS_SEARCH_DATE_DYNAMIC_WEEKDAYS = [
	'monday',
	'tuesday',
	'wednesday',
	'thursday',
	'friday',
	'saturday',
	'sunday',
] as const;
export const IDO_DETAILS_SEARCH_DATE_DYNAMIC_QUARTERS = ['q1', 'q2', 'q3', 'q4'] as const;
export const IDO_DETAILS_SEARCH_DATE_DYNAMIC_MONTHS = [
	'january',
	'february',
	'march',
	'april',
	'may',
	'june',
	'july',
	'august',
	'september',
	'october',
	'november',
	'december',
] as const;
const IDO_DETAILS_SEARCH_SUMMARY_TEXT_LIMIT = 30;
const IDO_DETAILS_SEARCH_SUMMARY_MULTI_VALUE_LIMIT = 50;
const IDO_DETAILS_SEARCH_LEGACY_CHOICE_ITEM_SUFFIX_PATTERN = /#NETZkultur_IdoModel_FieldTypes_Items_ChoiceItem_\d+$/;
const IDO_DETAILS_SEARCH_ALLOWED_OPERATIONS_BY_FIELD_TYPE: Record<
	IdoDetailsSearchFieldType,
	IdoDetailsSearchFieldOperation[]
> = {
	binary: ['isTrue', 'isNotTrue'],
	date: [
		'is',
		'isNot',
		'isBefore',
		'isAfter',
		'isInTheRange',
		'isNotInTheRange',
		'isInTheLast',
		'isNotInTheLast',
		'isInTheNext',
		'isNotInTheNext',
		'isEmpty',
		'isNotEmpty',
	],
	geoLocation: [
		'is',
		'isNot',
		'isLessThan',
		'isGreaterThan',
		'isBeginningWith',
		'isNotBeginningWith',
		'isEndingWith',
		'isNotEndingWith',
		'doesContain',
		'doesNotContain',
		'isEmpty',
		'isNotEmpty',
		'isInRadiusSearch',
	],
	multipleChoice: ['is', 'isNot', 'isAny', 'isNotAny', 'isAll', 'isNotAll', 'isEmpty', 'isNotEmpty'],
	singleChoice: ['is', 'isNot', 'isAny', 'isNotAny', 'isEmpty', 'isNotEmpty'],
	textarea: [
		'isBeginningWith',
		'isNotBeginningWith',
		'isEndingWith',
		'isNotEndingWith',
		'doesContain',
		'doesNotContain',
		'is',
		'isNot',
		'isEmpty',
		'isNotEmpty',
	],
	value: [
		'isLessThan',
		'isGreaterThan',
		'isBeginningWith',
		'isNotBeginningWith',
		'isEndingWith',
		'isNotEndingWith',
		'doesContain',
		'doesNotContain',
		'is',
		'isNot',
		'isEmpty',
		'isNotEmpty',
	],
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

const isIdoDetailsSearchTermOption = (value: unknown): value is IdoDetailsSearchTermOption =>
	isPlainObject(value) && typeof value.displayField === 'string' && typeof value.valueField === 'string';

const isString = (value: unknown): value is string => typeof value === 'string';

const isIdoDetailsSearchGroupOperation = (value: unknown): value is IdoDetailsSearchGroupOperation =>
	isString(value) && IDO_DETAILS_SEARCH_GROUP_OPERATIONS.includes(value as IdoDetailsSearchGroupOperation);

const isIdoDetailsSearchFieldType = (value: unknown): value is IdoDetailsSearchFieldType =>
	isString(value) && IDO_DETAILS_SEARCH_FIELD_TYPES.includes(value as IdoDetailsSearchFieldType);

const isIdoDetailsSearchFieldLocation = (value: unknown): value is IdoDetailsSearchFieldLocation =>
	isString(value) && IDO_DETAILS_SEARCH_FIELD_LOCATIONS.includes(value as IdoDetailsSearchFieldLocation);

const isIdoDetailsSearchFieldOperation = (value: unknown): value is IdoDetailsSearchFieldOperation =>
	isString(value) && IDO_DETAILS_SEARCH_FIELD_OPERATIONS.includes(value as IdoDetailsSearchFieldOperation);

const isIdoDetailsSearchDateValueOperation = (
	operation: IdoDetailsSearchFieldOperation
): operation is (typeof IDO_DETAILS_SEARCH_DATE_VALUE_OPERATIONS)[number] =>
	IDO_DETAILS_SEARCH_DATE_VALUE_OPERATIONS.includes(
		operation as (typeof IDO_DETAILS_SEARCH_DATE_VALUE_OPERATIONS)[number]
	);

const isIdoDetailsSearchDateRangeOperation = (
	operation: IdoDetailsSearchFieldOperation
): operation is (typeof IDO_DETAILS_SEARCH_DATE_RANGE_OPERATIONS)[number] =>
	IDO_DETAILS_SEARCH_DATE_RANGE_OPERATIONS.includes(
		operation as (typeof IDO_DETAILS_SEARCH_DATE_RANGE_OPERATIONS)[number]
	);

const isIdoDetailsSearchDateRelativeOperation = (
	operation: IdoDetailsSearchFieldOperation
): operation is (typeof IDO_DETAILS_SEARCH_DATE_RELATIVE_OPERATIONS)[number] =>
	IDO_DETAILS_SEARCH_DATE_RELATIVE_OPERATIONS.includes(
		operation as (typeof IDO_DETAILS_SEARCH_DATE_RELATIVE_OPERATIONS)[number]
	);

const isIdoDetailsSearchDateRelativeRange = (
	value: unknown
): value is (typeof IDO_DETAILS_SEARCH_DATE_RELATIVE_RANGES)[number] =>
	isString(value) &&
	IDO_DETAILS_SEARCH_DATE_RELATIVE_RANGES.includes(value as (typeof IDO_DETAILS_SEARCH_DATE_RELATIVE_RANGES)[number]);

const isIdoDetailsSearchDateType = (value: unknown): value is (typeof IDO_DETAILS_SEARCH_DATE_TYPES)[number] =>
	isString(value) && IDO_DETAILS_SEARCH_DATE_TYPES.includes(value as (typeof IDO_DETAILS_SEARCH_DATE_TYPES)[number]);

const isIdoDetailsSearchDateDynamicOperation = (
	value: unknown
): value is (typeof IDO_DETAILS_SEARCH_DATE_DYNAMIC_OPERATIONS)[number] =>
	isString(value) &&
	IDO_DETAILS_SEARCH_DATE_DYNAMIC_OPERATIONS.includes(
		value as (typeof IDO_DETAILS_SEARCH_DATE_DYNAMIC_OPERATIONS)[number]
	);

const isIdoDetailsSearchDateDynamicWeekday = (
	value: unknown
): value is (typeof IDO_DETAILS_SEARCH_DATE_DYNAMIC_WEEKDAYS)[number] =>
	isString(value) &&
	IDO_DETAILS_SEARCH_DATE_DYNAMIC_WEEKDAYS.includes(
		value as (typeof IDO_DETAILS_SEARCH_DATE_DYNAMIC_WEEKDAYS)[number]
	);

const isIdoDetailsSearchDateDynamicQuarter = (
	value: unknown
): value is (typeof IDO_DETAILS_SEARCH_DATE_DYNAMIC_QUARTERS)[number] =>
	isString(value) &&
	IDO_DETAILS_SEARCH_DATE_DYNAMIC_QUARTERS.includes(
		value as (typeof IDO_DETAILS_SEARCH_DATE_DYNAMIC_QUARTERS)[number]
	);

const isIdoDetailsSearchDateDynamicMonth = (
	value: unknown
): value is (typeof IDO_DETAILS_SEARCH_DATE_DYNAMIC_MONTHS)[number] =>
	isString(value) &&
	IDO_DETAILS_SEARCH_DATE_DYNAMIC_MONTHS.includes(value as (typeof IDO_DETAILS_SEARCH_DATE_DYNAMIC_MONTHS)[number]);

const isIdoDetailsSearchGeoLocationRadiusOperation = (
	operation: IdoDetailsSearchFieldOperation
): operation is typeof IDO_DETAILS_SEARCH_GEO_LOCATION_RADIUS_OPERATION =>
	operation === IDO_DETAILS_SEARCH_GEO_LOCATION_RADIUS_OPERATION;

const normalizeScalarTerm = (value: unknown): IdoDetailsSearchScalarTerm | undefined => {
	if (value === null || typeof value === 'number' || typeof value === 'string') {
		return value;
	}

	return undefined;
};

const normalizeArrayTerm = (value: unknown): IdoDetailsSearchScalarTerm[] | undefined => {
	if (!Array.isArray(value)) {
		return undefined;
	}

	const normalizedValues = value
		.map((entry) => normalizeScalarTerm(entry))
		.filter((entry): entry is IdoDetailsSearchScalarTerm => entry !== undefined);

	return normalizedValues;
};

const normalizeTerms = (value: unknown): IdoDetailsSearchTerms => {
	const scalarTerm = normalizeScalarTerm(value);
	if (scalarTerm !== undefined) {
		return scalarTerm;
	}

	const arrayTerm = normalizeArrayTerm(value);
	if (arrayTerm !== undefined) {
		return arrayTerm;
	}

	if (!isPlainObject(value)) {
		return {};
	}

	const normalizedTerms = Object.entries(value).reduce<Record<string, IdoDetailsSearchTermRecordValue>>(
		(accumulator, [key, rawValue]) => {
			const normalizedScalar = normalizeScalarTerm(rawValue);
			if (normalizedScalar !== undefined) {
				accumulator[key] = normalizedScalar;
				return accumulator;
			}

			const normalizedArray = normalizeArrayTerm(rawValue);
			if (normalizedArray !== undefined) {
				accumulator[key] = normalizedArray;
			}

			return accumulator;
		},
		{}
	);

	return normalizedTerms;
};

const normalizeTermsForFieldType = (_fieldType: IdoDetailsSearchFieldType, value: unknown): IdoDetailsSearchTerms => {
	return normalizeTerms(value);
};

const idoDetailsSearchFieldConditionUsesMultipleChoiceTerms = (
	fieldType: IdoDetailsSearchFieldType,
	operation: IdoDetailsSearchFieldOperation
): boolean => {
	if (fieldType !== 'singleChoice' && fieldType !== 'multipleChoice') {
		return false;
	}

	if (
		!IDO_DETAILS_SEARCH_CHOICE_MULTI_VALUE_OPERATIONS.includes(
			operation as (typeof IDO_DETAILS_SEARCH_CHOICE_MULTI_VALUE_OPERATIONS)[number]
		)
	) {
		return false;
	}

	if (fieldType === 'singleChoice') {
		return operation === 'isAny' || operation === 'isNotAny';
	}

	return true;
};

const normalizeTermsForFieldCondition = (
	fieldType: IdoDetailsSearchFieldType,
	operation: IdoDetailsSearchFieldOperation,
	value: unknown
): IdoDetailsSearchTerms => {
	if (fieldType === 'singleChoice' || fieldType === 'multipleChoice') {
		return idoDetailsSearchFieldConditionUsesMultipleChoiceTerms(fieldType, operation)
			? getIdoDetailsSearchMultipleChoiceTermsFromSelection(value)
			: getIdoDetailsSearchSingleChoiceTermsFromSelection(value);
	}

	return normalizeTermsForFieldType(fieldType, value);
};

const parseLegacyFieldCondition = (value: Record<string, unknown>): IdoDetailsSearchFieldCondition | null => {
	if (
		!isString(value.name) ||
		!isIdoDetailsSearchFieldType(value.type) ||
		!isIdoDetailsSearchFieldLocation(value.location) ||
		!isIdoDetailsSearchFieldOperation(value.operation)
	) {
		return null;
	}

	return {
		_key: typeof value._key === 'string' && value._key !== '' ? value._key : generateDragKey(),
		...(typeof value.displayField === 'string' ? { displayField: value.displayField } : {}),
		kind: 'fieldCondition',
		location: value.location,
		name: value.name,
		operation: value.operation,
		terms: normalizeTermsForFieldCondition(value.type, value.operation, value.terms),
		type: value.type,
	};
};

const parseLegacyNode = (value: unknown): IdoDetailsSearchNode | null => {
	if (!isPlainObject(value) || Object.keys(value).length === 0) {
		return null;
	}

	if (Array.isArray(value.fields)) {
		return parseLegacyGroup(value);
	}

	return parseLegacyFieldCondition(value);
};

const parseLegacyGroup = (value: Record<string, unknown>): IdoDetailsSearchGroup => {
	const children = Array.isArray(value.fields)
		? value.fields
				.map((childValue) => parseLegacyNode(childValue))
				.filter((childValue): childValue is IdoDetailsSearchNode => childValue !== null)
		: [];

	return {
		_key: typeof value._key === 'string' && value._key !== '' ? value._key : generateDragKey(),
		children,
		kind: 'group',
		logicalOperator: isIdoDetailsSearchGroupOperation(value.operation) ? value.operation : 'and',
	};
};

const serializeLegacyFieldCondition = (
	fieldCondition: IdoDetailsSearchFieldCondition
): LegacyIdoDetailsSearchFieldCondition => ({
	...(fieldCondition.displayField !== undefined ? { displayField: fieldCondition.displayField } : {}),
	location: fieldCondition.location,
	name: fieldCondition.name,
	operation: fieldCondition.operation,
	terms: fieldCondition.terms,
	type: fieldCondition.type,
});

const serializeLegacyGroup = (group: IdoDetailsSearchGroup, isRootGroup: boolean): LegacyIdoDetailsSearchGroup => {
	const fields =
		group.children.length === 0
			? [EMPTY_LEGACY_GROUP_CHILD]
			: group.children.map((child) =>
					child.kind === 'group' ? serializeLegacyGroup(child, false) : serializeLegacyFieldCondition(child)
				);

	if (isRootGroup && group.children.length === 0) {
		return { fields };
	}

	return {
		fields,
		operation: group.logicalOperator,
	};
};

const generateDragKey = (): string => Math.random().toString(36).slice(2);

export const createEmptyIdoDetailsSearchGroup = (
	logicalOperator: IdoDetailsSearchGroupOperation = 'and'
): IdoDetailsSearchGroup => ({
	_key: generateDragKey(),
	children: [],
	kind: 'group',
	logicalOperator,
});

export const parseLegacyIdoDetailsSearch = (serializedDetailsSearch: string): IdoDetailsSearchGroup => {
	const normalizedSerializedDetailsSearch = serializedDetailsSearch.trim();
	if (normalizedSerializedDetailsSearch === '') {
		return createEmptyIdoDetailsSearchGroup();
	}

	try {
		const parsedPayload = JSON.parse(normalizedSerializedDetailsSearch) as unknown;
		if (!Array.isArray(parsedPayload) || parsedPayload.length === 0 || !isPlainObject(parsedPayload[0])) {
			return createEmptyIdoDetailsSearchGroup();
		}

		return parseLegacyGroup(parsedPayload[0]);
	} catch {
		return createEmptyIdoDetailsSearchGroup();
	}
};

export const serializeLegacyIdoDetailsSearch = (detailsSearchGroup: IdoDetailsSearchGroup): string => {
	const payload: LegacyIdoDetailsSearchPayload = [serializeLegacyGroup(detailsSearchGroup, true)];
	return JSON.stringify(payload);
};

export const getIdoDetailsSearchAllowedOperationsForFieldType = (
	fieldType: IdoDetailsSearchFieldType
): IdoDetailsSearchFieldOperation[] => [...IDO_DETAILS_SEARCH_ALLOWED_OPERATIONS_BY_FIELD_TYPE[fieldType]];

const idoDetailsSearchOperationUsesTerms = (operation: IdoDetailsSearchFieldOperation): boolean =>
	!IDO_DETAILS_SEARCH_OPERATIONS_WITHOUT_TERMS.includes(
		operation as (typeof IDO_DETAILS_SEARCH_OPERATIONS_WITHOUT_TERMS)[number]
	);

export const idoDetailsSearchFieldConditionUsesAutocompleteHints = (
	fieldCondition: Pick<IdoDetailsSearchFieldCondition, 'operation' | 'type'>
): boolean => {
	if (fieldCondition.type !== 'value' && fieldCondition.type !== 'geoLocation') {
		return false;
	}

	return IDO_DETAILS_SEARCH_AUTOCOMPLETE_OPERATIONS.includes(
		fieldCondition.operation as (typeof IDO_DETAILS_SEARCH_AUTOCOMPLETE_OPERATIONS)[number]
	);
};

const createDefaultIdoDetailsSearchDateValueTerms = (
	operation: (typeof IDO_DETAILS_SEARCH_DATE_VALUE_OPERATIONS)[number]
): Record<string, string> => {
	const defaultTerms: Record<string, string> = {
		date: '',
		dateType: 'fixed',
	};

	if (operation === 'is') {
		defaultTerms.isFixedOperation = 'date';
	}

	if (operation === 'isNot') {
		defaultTerms.isNotFixedOperation = 'date';
	}

	return defaultTerms;
};

const getIdoDetailsSearchDateDynamicOperationKey = (
	operation: IdoDetailsSearchFieldOperation
): 'isDynamicOperation' | 'isNotDynamicOperation' | null => {
	if (operation === 'is') {
		return 'isDynamicOperation';
	}

	if (operation === 'isNot') {
		return 'isNotDynamicOperation';
	}

	return null;
};

const normalizeIdoDetailsSearchDateDynamicTextTerm = (value: unknown): string => {
	if (typeof value === 'number') {
		return `${value}`;
	}

	return typeof value === 'string' ? value : '';
};

const createDefaultIdoDetailsSearchDateRangeTerms = (): Record<'from' | 'to', string> => ({
	from: '',
	to: '',
});

const createDefaultIdoDetailsSearchDateRelativeTerms = (): {
	interval: number;
	range: (typeof IDO_DETAILS_SEARCH_DATE_RELATIVE_RANGES)[number];
} => ({
	interval: 1,
	range: 'day',
});

export const getIdoDetailsSearchDateValueTerms = (
	terms: IdoDetailsSearchTerms
): { date: string; isSupported: boolean } => {
	if (!isPlainObject(terms)) {
		return { date: '', isSupported: false };
	}

	if (terms.dateType !== undefined && terms.dateType !== 'fixed') {
		return { date: '', isSupported: false };
	}

	return {
		date: typeof terms.date === 'string' ? terms.date : '',
		isSupported: true,
	};
};

export const getIdoDetailsSearchDateDynamicTerms = (
	operation: IdoDetailsSearchFieldOperation,
	terms: IdoDetailsSearchTerms
): {
	dateType: (typeof IDO_DETAILS_SEARCH_DATE_TYPES)[number];
	dynamicOperation: (typeof IDO_DETAILS_SEARCH_DATE_DYNAMIC_OPERATIONS)[number];
	isSupported: boolean;
	monthOfCurrentYear: string;
	nthDayOfCurrentMonth: string;
	nthWeekdayOfCurrentMonthNumber: string;
	nthWeekdayOfCurrentMonthWeekday: string;
	quarterOfCurrentYear: string;
	weekdayOfCurrentCalendarWeek: string;
} => {
	const defaultTerms = {
		dateType: 'fixed' as const,
		dynamicOperation: 'today' as const,
		isSupported: false,
		monthOfCurrentYear: '',
		nthDayOfCurrentMonth: '',
		nthWeekdayOfCurrentMonthNumber: '',
		nthWeekdayOfCurrentMonthWeekday: '',
		quarterOfCurrentYear: '',
		weekdayOfCurrentCalendarWeek: '',
	};

	if (!isPlainObject(terms)) {
		return defaultTerms;
	}

	const dateType = isIdoDetailsSearchDateType(terms.dateType) ? terms.dateType : 'fixed';
	if (dateType !== 'dynamic') {
		return {
			...defaultTerms,
			dateType,
		};
	}

	const dynamicOperationKey = getIdoDetailsSearchDateDynamicOperationKey(operation);
	if (dynamicOperationKey === null || !isIdoDetailsSearchDateDynamicOperation(terms[dynamicOperationKey])) {
		return {
			...defaultTerms,
			dateType,
		};
	}

	return {
		dateType,
		dynamicOperation: terms[dynamicOperationKey],
		isSupported: true,
		monthOfCurrentYear: isIdoDetailsSearchDateDynamicMonth(terms.monthOfCurrentYear)
			? terms.monthOfCurrentYear
			: '',
		nthDayOfCurrentMonth: normalizeIdoDetailsSearchDateDynamicTextTerm(terms.nthDayOfCurrentMonth),
		nthWeekdayOfCurrentMonthNumber: normalizeIdoDetailsSearchDateDynamicTextTerm(
			terms.nthWeekdayOfCurrentMonthNumber
		),
		nthWeekdayOfCurrentMonthWeekday: isIdoDetailsSearchDateDynamicWeekday(terms.nthWeekdayOfCurrentMonthWeekday)
			? terms.nthWeekdayOfCurrentMonthWeekday
			: '',
		quarterOfCurrentYear: isIdoDetailsSearchDateDynamicQuarter(terms.quarterOfCurrentYear)
			? terms.quarterOfCurrentYear
			: '',
		weekdayOfCurrentCalendarWeek: isIdoDetailsSearchDateDynamicWeekday(terms.weekdayOfCurrentCalendarWeek)
			? terms.weekdayOfCurrentCalendarWeek
			: '',
	};
};

export const getIdoDetailsSearchDateDynamicNumericTermFromInput = (value: null | number | string): '' | number => {
	if (typeof value === 'number') {
		return Number.isFinite(value) && value > 0 ? value : '';
	}

	if (typeof value !== 'string') {
		return '';
	}

	const parsedValue = Number(value.trim());
	return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : '';
};

export const getIdoDetailsSearchDateRangeTerms = (
	terms: IdoDetailsSearchTerms
): { from: string; isSupported: boolean; to: string } => {
	if (!isPlainObject(terms)) {
		return { from: '', isSupported: false, to: '' };
	}

	return {
		from: typeof terms.from === 'string' ? terms.from : '',
		isSupported: true,
		to: typeof terms.to === 'string' ? terms.to : '',
	};
};

export const getIdoDetailsSearchDateRelativeTerms = (
	terms: IdoDetailsSearchTerms
): { interval: number; isSupported: boolean; range: (typeof IDO_DETAILS_SEARCH_DATE_RELATIVE_RANGES)[number] } => {
	if (
		!isPlainObject(terms) ||
		typeof terms.interval !== 'number' ||
		!isIdoDetailsSearchDateRelativeRange(terms.range)
	) {
		return {
			...createDefaultIdoDetailsSearchDateRelativeTerms(),
			isSupported: false,
		};
	}

	return {
		interval: terms.interval,
		isSupported: true,
		range: terms.range,
	};
};

export const getIdoDetailsSearchSingleChoiceTermsFromSelection = (selection: unknown): string => {
	if (typeof selection === 'string') {
		return selection;
	}

	if (Array.isArray(selection)) {
		for (const selectionValue of selection) {
			const normalizedSelectionValue = getIdoDetailsSearchSingleChoiceTermsFromSelection(selectionValue);
			if (normalizedSelectionValue !== '') {
				return normalizedSelectionValue;
			}
		}

		return '';
	}

	if (isIdoDetailsSearchTermOption(selection)) {
		return selection.valueField;
	}

	return '';
};

export const getIdoDetailsSearchMultipleChoiceTermsFromSelection = (selection: unknown): string[] => {
	if (Array.isArray(selection)) {
		return selection
			.map((selectionValue) => getIdoDetailsSearchSingleChoiceTermsFromSelection(selectionValue))
			.filter((selectionValue): selectionValue is string => selectionValue !== '');
	}

	const singleSelection = getIdoDetailsSearchSingleChoiceTermsFromSelection(selection);
	return singleSelection === '' ? [] : [singleSelection];
};

export const matchIdoDetailsSearchTermOption = (
	rawValue: string,
	termOptions: IdoDetailsSearchTermOption[]
): IdoDetailsSearchTermOption | null => {
	const normalizedRawValue = rawValue.trim();
	if (normalizedRawValue === '') {
		return null;
	}

	const exactMatch = termOptions.find(
		(termOption) => termOption.valueField === normalizedRawValue || termOption.displayField === normalizedRawValue
	);
	if (exactMatch !== undefined) {
		return exactMatch;
	}

	const legacyChoiceItemSuffix = normalizedRawValue.match(IDO_DETAILS_SEARCH_LEGACY_CHOICE_ITEM_SUFFIX_PATTERN)?.[0];
	if (legacyChoiceItemSuffix === undefined) {
		return null;
	}

	const legacyChoiceItemMatch = termOptions.find((termOption) =>
		termOption.valueField.endsWith(legacyChoiceItemSuffix)
	);
	return legacyChoiceItemMatch ?? null;
};

export const resolveIdoDetailsSearchTermLabel = (
	rawValue: string,
	termOptions: IdoDetailsSearchTermOption[]
): string => {
	const matchingTermOption = matchIdoDetailsSearchTermOption(rawValue, termOptions);
	return matchingTermOption?.displayField ?? rawValue;
};

interface IdoDetailsSearchChoiceTermsReconciliation {
	hasChanges: boolean;
	terms: string[];
}

export const reconcileIdoDetailsSearchChoiceTerms = (
	terms: readonly string[],
	termOptions: IdoDetailsSearchTermOption[]
): IdoDetailsSearchChoiceTermsReconciliation => {
	if (termOptions.length === 0) {
		return { hasChanges: false, terms: [...terms] };
	}

	const reconciledTerms: string[] = [];

	for (const term of terms) {
		const matchingTermOption = matchIdoDetailsSearchTermOption(term, termOptions);
		if (matchingTermOption === null || reconciledTerms.includes(matchingTermOption.valueField)) {
			continue;
		}

		reconciledTerms.push(matchingTermOption.valueField);
	}

	return {
		hasChanges:
			reconciledTerms.length !== terms.length ||
			reconciledTerms.some((reconciledTerm, index) => reconciledTerm !== terms[index]),
		terms: reconciledTerms,
	};
};

const createDefaultIdoDetailsSearchGeoLocationRadiusTerms = (): {
	km: string;
	postalCode: string;
	postalCodeCountry: string;
} => ({
	km: '',
	postalCode: '',
	postalCodeCountry: 'de_DE',
});

export const getIdoDetailsSearchGeoLocationRadiusTerms = (
	terms: IdoDetailsSearchTerms
): { isSupported: boolean; km: string; postalCode: string; postalCodeCountry: string } => {
	if (!isPlainObject(terms)) {
		return {
			...createDefaultIdoDetailsSearchGeoLocationRadiusTerms(),
			isSupported: false,
		};
	}

	return {
		isSupported: true,
		km: typeof terms.km === 'string' ? terms.km : '',
		postalCode: typeof terms.postalCode === 'string' ? terms.postalCode : '',
		postalCodeCountry:
			typeof terms.postalCodeCountry === 'string' && terms.postalCodeCountry !== ''
				? terms.postalCodeCountry
				: 'de_DE',
	};
};

const createDefaultIdoDetailsSearchTerms = (
	fieldType: IdoDetailsSearchFieldType,
	operation: IdoDetailsSearchFieldOperation
): IdoDetailsSearchTerms => {
	if (!idoDetailsSearchOperationUsesTerms(operation)) {
		return null;
	}

	if (fieldType === 'singleChoice' || fieldType === 'multipleChoice') {
		return idoDetailsSearchFieldConditionUsesMultipleChoiceTerms(fieldType, operation) ? [] : '';
	}

	if (fieldType === 'date' && isIdoDetailsSearchDateValueOperation(operation)) {
		return createDefaultIdoDetailsSearchDateValueTerms(operation);
	}

	if (fieldType === 'date' && isIdoDetailsSearchDateRangeOperation(operation)) {
		return createDefaultIdoDetailsSearchDateRangeTerms();
	}

	if (fieldType === 'date' && isIdoDetailsSearchDateRelativeOperation(operation)) {
		return createDefaultIdoDetailsSearchDateRelativeTerms();
	}

	if (fieldType === 'geoLocation' && isIdoDetailsSearchGeoLocationRadiusOperation(operation)) {
		return createDefaultIdoDetailsSearchGeoLocationRadiusTerms();
	}

	if (fieldType === 'date') {
		return {};
	}

	return '';
};

const truncateIdoDetailsSearchSummary = (value: string, maxLength: number): string => {
	const normalizedValue = value.trim();
	if (normalizedValue.length <= maxLength) {
		return normalizedValue;
	}

	return `${normalizedValue.slice(0, Math.max(0, maxLength - 3))}...`;
};

const getIdoDetailsSearchSummaryObjectTerm = (terms: IdoDetailsSearchTerms): string => {
	if (!isPlainObject(terms)) {
		return '';
	}

	const preferredKeys = [
		'date',
		'from',
		'postalCode',
		'interval',
		'to',
		'monthOfCurrentYear',
		'quarterOfCurrentYear',
		'weekdayOfCurrentCalendarWeek',
		'nthDayOfCurrentMonth',
		'nthWeekdayOfCurrentMonthNumber',
		'nthWeekdayOfCurrentMonthWeekday',
	];

	for (const preferredKey of preferredKeys) {
		const preferredValue = terms[preferredKey];
		if (typeof preferredValue === 'number') {
			return `${preferredValue}`;
		}

		if (typeof preferredValue === 'string' && preferredValue.trim() !== '') {
			return preferredValue;
		}
	}

	for (const value of Object.values(terms)) {
		if (typeof value === 'number') {
			return `${value}`;
		}

		if (typeof value === 'string' && value.trim() !== '') {
			return value;
		}

		if (Array.isArray(value)) {
			const firstArrayValue =
				value
					.filter((entry): entry is number | string => typeof entry === 'number' || typeof entry === 'string')
					.map((entry) => `${entry}`.trim())
					.find((entry) => entry !== '') ?? '';
			if (firstArrayValue !== '') {
				return firstArrayValue;
			}
		}
	}

	return '';
};

const buildIdoDetailsSearchTermsSummary = (
	condition: IdoDetailsSearchFieldCondition,
	resolveTermLabel?: (rawValue: string) => string
): string => {
	if (!idoDetailsSearchOperationUsesTerms(condition.operation) || condition.terms === null) {
		return '';
	}

	if (typeof condition.terms === 'number') {
		return `${condition.terms}`;
	}

	if (typeof condition.terms === 'string') {
		const resolvedTermLabel = resolveTermLabel?.(condition.terms) ?? condition.terms;
		return truncateIdoDetailsSearchSummary(resolvedTermLabel, IDO_DETAILS_SEARCH_SUMMARY_TEXT_LIMIT);
	}

	if (Array.isArray(condition.terms)) {
		return truncateIdoDetailsSearchSummary(
			condition.terms
				.map((term) => {
					const rawTermValue = `${term}`;
					return resolveTermLabel?.(rawTermValue) ?? rawTermValue;
				})
				.join(', '),
			IDO_DETAILS_SEARCH_SUMMARY_MULTI_VALUE_LIMIT
		);
	}

	return truncateIdoDetailsSearchSummary(
		getIdoDetailsSearchSummaryObjectTerm(condition.terms),
		IDO_DETAILS_SEARCH_SUMMARY_TEXT_LIMIT
	);
};

const isBlankIdoDetailsSearchText = (value: unknown): boolean => typeof value !== 'string' || value.trim() === '';

const hasEmptyRequiredIdoDetailsSearchDateTerms = (condition: IdoDetailsSearchFieldCondition): boolean => {
	if (isIdoDetailsSearchDateValueOperation(condition.operation)) {
		const dynamicTerms = getIdoDetailsSearchDateDynamicTerms(condition.operation, condition.terms);
		if (dynamicTerms.dateType === 'dynamic') {
			if (!dynamicTerms.isSupported) {
				return true;
			}

			if (dynamicTerms.dynamicOperation === 'weekdayOfCurrentCalendarWeek') {
				return dynamicTerms.weekdayOfCurrentCalendarWeek === '';
			}

			if (dynamicTerms.dynamicOperation === 'monthOfCurrentYear') {
				return dynamicTerms.monthOfCurrentYear === '';
			}

			if (dynamicTerms.dynamicOperation === 'quarterOfCurrentYear') {
				return dynamicTerms.quarterOfCurrentYear === '';
			}

			if (dynamicTerms.dynamicOperation === 'nthDayOfCurrentMonth') {
				return dynamicTerms.nthDayOfCurrentMonth === '';
			}

			if (dynamicTerms.dynamicOperation === 'nthWeekdayOfCurrentMonth') {
				return (
					dynamicTerms.nthWeekdayOfCurrentMonthNumber === '' ||
					dynamicTerms.nthWeekdayOfCurrentMonthWeekday === ''
				);
			}

			return false;
		}

		const dateValueTerms = getIdoDetailsSearchDateValueTerms(condition.terms);
		return !dateValueTerms.isSupported || dateValueTerms.date.trim() === '';
	}

	if (isIdoDetailsSearchDateRangeOperation(condition.operation)) {
		const dateRangeTerms = getIdoDetailsSearchDateRangeTerms(condition.terms);
		return !dateRangeTerms.isSupported || dateRangeTerms.from.trim() === '' || dateRangeTerms.to.trim() === '';
	}

	if (isIdoDetailsSearchDateRelativeOperation(condition.operation)) {
		const relativeTerms = getIdoDetailsSearchDateRelativeTerms(condition.terms);
		return !relativeTerms.isSupported || relativeTerms.interval <= 0 || relativeTerms.range.trim() === '';
	}

	return false;
};

const idoDetailsSearchFieldConditionHasEmptyTerms = (condition: IdoDetailsSearchFieldCondition): boolean => {
	switch (getIdoDetailsSearchTermEditorKind(condition)) {
		case 'none':
			return false;
		case 'text':
			return isBlankIdoDetailsSearchText(condition.terms);
		case 'singleChoice':
			return typeof condition.terms !== 'string' || condition.terms === '';
		case 'multipleChoice':
			return !Array.isArray(condition.terms) || condition.terms.length === 0;
		case 'date':
			return hasEmptyRequiredIdoDetailsSearchDateTerms(condition);
		case 'geoLocation':
			if (isIdoDetailsSearchGeoLocationRadiusOperation(condition.operation)) {
				const radiusTerms = getIdoDetailsSearchGeoLocationRadiusTerms(condition.terms);
				return !radiusTerms.isSupported || radiusTerms.km === '' || radiusTerms.postalCode === '';
			}

			return isBlankIdoDetailsSearchText(condition.terms);
		case 'unsupported':
			return false;
	}
};

export const getIdoDetailsSearchTermEditorKind = (
	fieldCondition: Pick<IdoDetailsSearchFieldCondition, 'operation' | 'type'>
): IdoDetailsSearchTermEditorKind => {
	if (!idoDetailsSearchOperationUsesTerms(fieldCondition.operation)) {
		return 'none';
	}

	if (fieldCondition.type === 'singleChoice' || fieldCondition.type === 'multipleChoice') {
		return idoDetailsSearchFieldConditionUsesMultipleChoiceTerms(fieldCondition.type, fieldCondition.operation)
			? 'multipleChoice'
			: 'singleChoice';
	}

	if (fieldCondition.type === 'value' || fieldCondition.type === 'textarea') {
		return 'text';
	}

	if (fieldCondition.type === 'date') {
		return 'date';
	}

	if (fieldCondition.type === 'geoLocation') {
		return 'geoLocation';
	}

	return 'unsupported';
};

export const createIdoDetailsSearchFieldConditionFromDefinition = (
	fieldDefinition: IdoDetailsSearchFieldDefinition
): IdoDetailsSearchFieldCondition => {
	const operation = getIdoDetailsSearchAllowedOperationsForFieldType(
		fieldDefinition.type
	)[0] as IdoDetailsSearchFieldOperation;

	return {
		_key: generateDragKey(),
		...(fieldDefinition.displayField !== undefined ? { displayField: fieldDefinition.displayField } : {}),
		kind: 'fieldCondition',
		location: fieldDefinition.location,
		name: fieldDefinition.name,
		operation,
		terms: createDefaultIdoDetailsSearchTerms(fieldDefinition.type, operation),
		type: fieldDefinition.type,
	};
};

const findIdoDetailsSearchFieldDefinition = (
	fieldCondition: Pick<IdoDetailsSearchFieldCondition, 'location' | 'name'>,
	fieldDefinitions: IdoDetailsSearchFieldDefinition[]
): IdoDetailsSearchFieldDefinition | undefined =>
	fieldDefinitions.find(
		(fieldDefinition) =>
			fieldDefinition.location === fieldCondition.location && fieldDefinition.name === fieldCondition.name
	);

const getSynchronizedIdoDetailsSearchOperationForDefinition = (
	fieldCondition: IdoDetailsSearchFieldCondition,
	fieldDefinition: IdoDetailsSearchFieldDefinition
): IdoDetailsSearchFieldOperation => {
	const allowedOperations = getIdoDetailsSearchAllowedOperationsForFieldType(fieldDefinition.type);

	if (allowedOperations.includes(fieldCondition.operation)) {
		return fieldCondition.operation;
	}

	return allowedOperations[0] as IdoDetailsSearchFieldOperation;
};

const getSynchronizedIdoDetailsSearchTermsForDefinition = (
	fieldCondition: IdoDetailsSearchFieldCondition,
	fieldDefinition: IdoDetailsSearchFieldDefinition,
	operation: IdoDetailsSearchFieldOperation
): IdoDetailsSearchTerms =>
	idoDetailsSearchOperationUsesTerms(operation)
		? normalizeTermsForFieldCondition(fieldDefinition.type, operation, fieldCondition.terms)
		: createDefaultIdoDetailsSearchTerms(fieldDefinition.type, operation);

const synchronizeIdoDetailsSearchFieldConditionWithDefinition = (
	fieldCondition: IdoDetailsSearchFieldCondition,
	fieldDefinition: IdoDetailsSearchFieldDefinition
): IdoDetailsSearchFieldCondition => {
	const operation = getSynchronizedIdoDetailsSearchOperationForDefinition(fieldCondition, fieldDefinition);

	return {
		...fieldCondition,
		displayField: fieldDefinition.displayField,
		location: fieldDefinition.location,
		name: fieldDefinition.name,
		operation,
		terms: getSynchronizedIdoDetailsSearchTermsForDefinition(fieldCondition, fieldDefinition, operation),
		type: fieldDefinition.type,
	};
};

export const synchronizeIdoDetailsSearchGroupWithFieldDefinitions = (
	detailsSearchGroup: IdoDetailsSearchGroup,
	fieldDefinitions: IdoDetailsSearchFieldDefinition[]
): IdoDetailsSearchGroup => ({
	...detailsSearchGroup,
	children: detailsSearchGroup.children.map((child) => {
		if (child.kind === 'group') {
			return synchronizeIdoDetailsSearchGroupWithFieldDefinitions(child, fieldDefinitions);
		}

		const matchingFieldDefinition = findIdoDetailsSearchFieldDefinition(child, fieldDefinitions);
		return matchingFieldDefinition === undefined
			? child
			: synchronizeIdoDetailsSearchFieldConditionWithDefinition(child, matchingFieldDefinition);
	}),
});

export const reconfigureIdoDetailsSearchFieldConditionForDefinition = (
	fieldCondition: IdoDetailsSearchFieldCondition,
	fieldDefinition: IdoDetailsSearchFieldDefinition
): IdoDetailsSearchFieldCondition => {
	const allowedOperations = getIdoDetailsSearchAllowedOperationsForFieldType(fieldDefinition.type);
	const operation = (
		allowedOperations.includes(fieldCondition.operation) ? fieldCondition.operation : allowedOperations[0]
	) as IdoDetailsSearchFieldOperation;

	return {
		...fieldCondition,
		...(fieldDefinition.displayField !== undefined ? { displayField: fieldDefinition.displayField } : {}),
		location: fieldDefinition.location,
		name: fieldDefinition.name,
		operation,
		terms: createDefaultIdoDetailsSearchTerms(fieldDefinition.type, operation),
		type: fieldDefinition.type,
	};
};

export const reconfigureIdoDetailsSearchFieldConditionOperation = (
	fieldCondition: IdoDetailsSearchFieldCondition,
	operation: IdoDetailsSearchFieldOperation
): IdoDetailsSearchFieldCondition => ({
	...fieldCondition,
	operation,
	terms: createDefaultIdoDetailsSearchTerms(fieldCondition.type, operation),
});

export const countIdoDetailsSearchFieldConditions = (detailsSearchGroup: IdoDetailsSearchGroup): number =>
	detailsSearchGroup.children.reduce<number>((conditionCount, child) => {
		if (child.kind === 'fieldCondition') {
			return conditionCount + 1;
		}

		return conditionCount + countIdoDetailsSearchFieldConditions(child);
	}, 0);

const pruneIdoDetailsSearchEmptyConditions = (detailsSearchGroup: IdoDetailsSearchGroup): IdoDetailsSearchGroup => ({
	...detailsSearchGroup,
	children: detailsSearchGroup.children
		.map((child) => (child.kind === 'group' ? pruneIdoDetailsSearchEmptyConditions(child) : child))
		.filter((child) =>
			child.kind === 'group' ? child.children.length > 0 : !idoDetailsSearchFieldConditionHasEmptyTerms(child)
		),
});

export const normalizeSerializedIdoDetailsSearch = (serializedDetailsSearch: string): string => {
	const parsedDetailsSearch = pruneIdoDetailsSearchEmptyConditions(
		parseLegacyIdoDetailsSearch(serializedDetailsSearch)
	);
	if (countIdoDetailsSearchFieldConditions(parsedDetailsSearch) === 0) {
		return EMPTY_SERIALIZED_IDO_DETAILS_SEARCH;
	}

	return serializeLegacyIdoDetailsSearch(parsedDetailsSearch);
};

export const normalizeSharedQuerySerializedIdoDetailsSearch = (
	serializedDetailsSearch: null | string | undefined
): string => {
	if (typeof serializedDetailsSearch !== 'string' || serializedDetailsSearch.trim() === '') {
		return EMPTY_SERIALIZED_IDO_DETAILS_SEARCH;
	}

	const normalizedSerializedDetailsSearch = normalizeSerializedIdoDetailsSearch(serializedDetailsSearch);
	if (normalizedSerializedDetailsSearch !== EMPTY_SERIALIZED_IDO_DETAILS_SEARCH) {
		return normalizedSerializedDetailsSearch;
	}

	try {
		return normalizeSerializedIdoDetailsSearch(decodeBase64(serializedDetailsSearch));
	} catch {
		return EMPTY_SERIALIZED_IDO_DETAILS_SEARCH;
	}
};

export const isIdoDetailsSearchDraftDirty = (
	detailsSearchDraft: IdoDetailsSearchGroup,
	appliedSerializedDetailsSearch: string
): boolean =>
	normalizeSerializedIdoDetailsSearch(serializeLegacyIdoDetailsSearch(detailsSearchDraft)) !==
	normalizeSerializedIdoDetailsSearch(appliedSerializedDetailsSearch);

export const summarizeIdoDetailsSearchCondition = (
	condition: IdoDetailsSearchFieldCondition,
	t: (key: string) => string,
	resolveTermLabel?: (rawValue: string) => string
): string => {
	const fieldLabel = condition.displayField ?? condition.name;
	const operationLabel = t(`ido_details_search_operation_${condition.operation}`);
	const termsLabel = buildIdoDetailsSearchTermsSummary(condition, resolveTermLabel);

	return termsLabel === '' ? `${fieldLabel} ${operationLabel}` : `${fieldLabel} ${operationLabel} ${termsLabel}`;
};

export const hasIdoDetailsSearchEmptyRequiredTerms = (group: IdoDetailsSearchGroup): boolean =>
	group.children.some((child) =>
		child.kind === 'group'
			? hasIdoDetailsSearchEmptyRequiredTerms(child)
			: idoDetailsSearchFieldConditionHasEmptyTerms(child)
	);

const cloneIdoDetailsSearchTerms = (terms: IdoDetailsSearchTerms): IdoDetailsSearchTerms => {
	if (terms === null || typeof terms === 'string' || typeof terms === 'number') {
		return terms;
	}
	if (Array.isArray(terms)) {
		return [...terms];
	}
	return { ...terms };
};

const cloneIdoDetailsSearchFieldCondition = (
	condition: IdoDetailsSearchFieldCondition
): IdoDetailsSearchFieldCondition => ({
	...condition,
	terms: cloneIdoDetailsSearchTerms(condition.terms),
});

const cloneIdoDetailsSearchNode = (node: IdoDetailsSearchNode): IdoDetailsSearchNode =>
	node.kind === 'fieldCondition' ? cloneIdoDetailsSearchFieldCondition(node) : cloneIdoDetailsSearchGroup(node);

export const cloneIdoDetailsSearchGroup = (detailsSearchGroup: IdoDetailsSearchGroup): IdoDetailsSearchGroup => ({
	...detailsSearchGroup,
	children: detailsSearchGroup.children.map(cloneIdoDetailsSearchNode),
});

export { IDO_DETAILS_SEARCH_FIELD_LOCATIONS, IDO_DETAILS_SEARCH_FIELD_OPERATIONS };
